import {useEffect, useState} from 'react';
import {getCountriesService, getStatesService} from '../services/geo.service';

/*
 * Countries and states from the API's geo master (GET /v1/meta/countries…), so this app
 * shows the same list as every other DQ app. Names are in the page's locale when the
 * master has a translation, English otherwise. Answers are cached per locale for the
 * page load; a failed request is not cached, so the next mount tries again.
 *
 * Save codes: a country is its ISO 3166-1 alpha-2 code, a state its ISO 3166-2 code.
 */

const cache = new Map(); // key → Promise

const cached = (key, load) => {
    if (!cache.has(key)) {
        cache.set(key, load().catch((e) => { cache.delete(key); throw e; }));
    }
    return cache.get(key);
};

const lc = (locale) => String(locale || 'en').toLowerCase();

/** [{ code, name, dial_code, has_states }] named in `locale`, sorted by that name. */
export const loadCountries = (locale) => cached(`countries|${lc(locale)}`, () =>
    getCountriesService(lc(locale)).then((res) => (res && res.data && res.data.countries) || []));

/**
 * A country's states as [{ code, name, label }]: `name` is the English name (what the
 * profile stores), `label` the name in `locale`, sorted by the label.
 */
export const loadStates = (countryCode, locale) => {
    const cc = String(countryCode || '').trim().toUpperCase();
    if (!cc) return Promise.resolve([]);
    const l = lc(locale);
    const fetchIn = (loc) => cached(`states|${cc}|${loc}`, () =>
        getStatesService(cc, loc).then((res) => (res && res.data && res.data.states) || []));
    return Promise.all([fetchIn('en'), l === 'en' ? null : fetchIn(l)]).then(([en, local]) => {
        const labelByCode = new Map((local || en).map((s) => [s.code, s.name]));
        return en
            .map((s) => ({code: s.code, name: s.name, label: labelByCode.get(s.code) || s.name}))
            .sort((a, b) => a.label.localeCompare(b.label));
    });
};

/** The country list for `locale`; null until it loads, or if it cannot be loaded. */
export const useCountries = (locale) => {
    const [countries, setCountries] = useState(null);
    useEffect(() => {
        let alive = true;
        loadCountries(locale)
            .then((list) => { if (alive) setCountries(list.length ? list : null); })
            .catch(() => { if (alive) setCountries(null); });
        return () => { alive = false; };
    }, [locale]);
    return countries;
};

/** `{ states, loading }` for one country; `states` is [] for a country without any. */
export const useStates = (countryCode, locale) => {
    const [result, setResult] = useState({key: '', states: []});
    const key = `${String(countryCode || '').trim().toUpperCase()}|${lc(locale)}`;
    useEffect(() => {
        let alive = true;
        if (!countryCode) { setResult({key, states: []}); return undefined; }
        loadStates(countryCode, locale)
            .then((states) => { if (alive) setResult({key, states}); })
            .catch(() => { if (alive) setResult({key, states: []}); });
        return () => { alive = false; };
    }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
    const current = result.key === key;
    return {states: current ? result.states : [], loading: !!countryCode && !current};
};
